import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Users, Brain, Shield,
  LineChart, Award, Bell, Target, Database,
  Check, ArrowRight, Sparkles, Lock, Eye,
  TrendingUp, TrendingDown, ShieldCheck,
} from 'lucide-react';
import PublicNav from '../../components/publicNav/PublicNav';
// Real components, reused directly rather than approximated -- same
// principle HomePage.jsx already applies (see "feedback_landing_page_
// component_reuse" memory): a features page selling the Credibility
// Protocol should show the real chip, not a mockup of one.
import CredibilityChip from '../../components/Credibility/CredibilityChip';
import GroupCredibilityChip from '../../pages/Groups/groupPageComponents/GroupCredibilityChip';

// Tier keys per feature item, parallel to the translated `features.items.*`
// arrays -- kept as internal (untranslated) identifiers, not display text,
// looked up against `features.tiers.*` at render time.
const TIER_KEYS = {
  sentiment: ['freeUpTo3', 'freeUpTo3'],
  credibility: ['freeUpTo3', 'bundleMoreThan3'],
  analytics: ['bundlePerTicker', 'bundlePerTicker'],
  social: ['free', 'free', 'free', 'bundle'],
  technical: ['bundle', 'bundle'],
  alerts: ['free', 'free', 'bundle', 'bundle'],
};

const API_ENDPOINTS = [
  '/api/v1/sentimentaggs/{ticker}',
  '/api/v1/stocks_news_sentiment/{ticker}',
  '/api/v1/stocks_charts/{ticker}/data',
  '/api/v1/market-snapshot/{symbol}',
  '/api/v1/edgar/search/with-summaries',
  '/api/v1/context-radar/ticker/{ticker}',
  '/api/v1/stocker/{ticker}/signal',
  '/api/v1/credibility/{user_id}',
  '/api/v1/engagement_scores/stock/{stock_id}/aggregated',
];

const FeaturesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('sentiment');

  const categories = [
    { id: 'sentiment', name: t('features.categories.sentiment'), icon: Brain },
    { id: 'credibility', name: t('features.categories.credibility'), icon: Award },
    { id: 'analytics', name: t('features.categories.analytics'), icon: Target },
    { id: 'social', name: t('features.categories.social'), icon: Users },
    { id: 'technical', name: t('features.categories.technical'), icon: LineChart },
    { id: 'alerts', name: t('features.categories.alerts'), icon: Bell },
  ];

  const activeItems = t(`features.items.${activeCategory}`, { returnObjects: true });
  const activeTierKeys = TIER_KEYS[activeCategory];

  const apiFeatures = t('features.api.endpoints', { returnObjects: true }).map((api, i) => ({
    ...api,
    endpoint: API_ENDPOINTS[i],
  }));

  // Real component reuse for the two categories with an obvious visual --
  // no generic "Illustrative layout only" placeholder box, since that told
  // a visitor less about the product than just not showing anything would
  // have. Categories without a natural single-component visual (Sentiment,
  // Predictions, Technical, Notifications) render no demo box at all.
  const FeatureDemo = ({ category, index }) => {
    if (category === 'credibility' && index === 0) {
      return (
        <div className="bg-gray-100 dark:bg-gray-800/20 border border-gray-200 dark:border-gray-700 rounded-lg p-6 flex flex-wrap items-center justify-center gap-3">
          <CredibilityChip cred={{ score: 320, tier: 'Beginner' }} size="sm" />
          <CredibilityChip cred={{ score: 612, tier: 'Active', accuracyPct: 58 }} size="sm" />
          <CredibilityChip cred={{ score: 862, tier: 'Elite', accuracyPct: 71 }} size="sm" />
        </div>
      );
    }
    if (category === 'social' && index === 0) {
      return (
        <div className="bg-gray-100 dark:bg-gray-800/20 border border-gray-200 dark:border-gray-700 rounded-lg p-6 flex items-center justify-center">
          <GroupCredibilityChip credibility={{ average_credibility: 728.4, total_credibility: 45890, member_count: 63 }} />
        </div>
      );
    }
    if (category === 'social' && index === 2) {
      return (
        <div className="bg-gray-100 dark:bg-gray-800/20 border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex items-center justify-center gap-3">
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border-2 bg-gradient-to-r from-emerald-100 to-emerald-50 text-emerald-700 border-emerald-400 dark:from-emerald-600/30 dark:to-emerald-500/30 dark:text-emerald-300 dark:border-emerald-500/50">
            <TrendingUp size={14} /> Bull
          </span>
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border-2 bg-gray-100 text-gray-500 border-gray-300 dark:bg-gray-700/30 dark:text-gray-400 dark:border-gray-600/50">
            <TrendingDown size={14} /> Bear
          </span>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className="w-full min-h-screen relative overflow-hidden bg-white dark:bg-[#09090f]"
      style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}
    >
      <Helmet>
        <title>Features | Stocker Beta</title>
        <meta
          name="description"
          content="Credibility Score, credibility-weighted Groups, gated Creator Access, and Published News -- how Stocker's four pillars work together."
        />
        <link rel="canonical" href="https://stocker-beta.com/features" />
      </Helmet>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');`}</style>
      <div
        style={{
          position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
          width: 900, height: 480,
          background: 'radial-gradient(ellipse at 50% -10%, rgba(109,40,217,0.16) 0%, transparent 65%)',
          pointerEvents: 'none',
        }}
      />

      {/* Navigation (shared across every public page) */}
      <PublicNav />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 py-16 text-center">
        <div className="inline-flex items-center gap-2 bg-violet-50 dark:bg-violet-500/20 px-4 py-2 rounded-full border border-violet-300 dark:border-violet-500/30 mb-6">
          <Sparkles className="text-violet-600 dark:text-violet-400" size={16} />
          <span className="text-sm text-violet-700 dark:text-violet-300">
            {t('features.hero.badge')}
          </span>
        </div>

        <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
          {t('features.hero.titleLine1')}<br />{t('features.hero.titleLine2')}
        </h1>

        <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-6">
          {t('features.hero.subtitle')}
        </p>

        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('features.hero.disclaimer')}
        </p>
      </section>


      {/* Category Navigation */}
      <section className="max-w-7xl mx-auto px-4 mb-12">
        <div className="flex flex-wrap gap-3 justify-center">
          {categories.map(cat => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                  activeCategory === cat.id
                    ? 'bg-violet-600 text-white shadow-lg'
                    : 'bg-cream-900/[0.03] text-gray-600 hover:bg-cream-900/[0.06] border border-violet-300 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10 dark:border-violet-500/20'
                }`}
              >
                <Icon size={20} />
                {cat.name}
              </button>
            );
          })}
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-7xl mx-auto px-4 pb-20">
        <div className="grid lg:grid-cols-2 gap-8">
          {activeItems.map((feature, i) => (
            <div key={i} className="bg-cream-900/[0.03] dark:bg-white/5 backdrop-blur-sm border border-violet-300 dark:border-violet-500/20 rounded-2xl p-8 hover:border-violet-500/40 transition-all">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{feature.name}</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">{feature.description}</p>
                </div>
                <span className="bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ml-4">
                  {t(`features.tiers.${activeTierKeys[i]}`)}
                </span>
              </div>

              <FeatureDemo category={activeCategory} index={i} />

              <div className="mt-6 space-y-2">
                {feature.benefits.map((benefit, j) => (
                  <div key={j} className="flex items-start gap-2">
                    <Check className="text-green-700 dark:text-green-400 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm text-gray-600 dark:text-gray-300">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* API Section */}
      <section className="bg-cream-900/[0.04] dark:bg-black/30 backdrop-blur-sm py-20 border-y border-violet-300 dark:border-violet-500/20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <Database className="text-violet-600 dark:text-violet-400 mx-auto mb-4" size={48} />
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">{t('features.api.sectionTitle')}</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              {t('features.api.sectionSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {apiFeatures.map((api, i) => (
              <div key={i} className="bg-cream-900/[0.03] dark:bg-white/5 backdrop-blur-sm border border-violet-300 dark:border-violet-500/20 rounded-lg p-6">
                <code className="text-violet-700 dark:text-violet-400 font-mono text-sm bg-violet-100 dark:bg-violet-500/10 px-3 py-1 rounded block mb-3">
                  {api.endpoint}
                </code>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">{api.description}</p>
                <p className="text-xs text-gray-500">{t('features.api.rateLimitPrefix')} {t('features.api.standardLimits')}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => navigate('/contact?type=sales')}
              className="bg-violet-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-violet-500 transition-all shadow-lg inline-flex items-center gap-2"
            >
              <Database size={20} />
              {t('features.api.requestAccess')}
            </button>
          </div>
        </div>
      </section>

      {/* Trust & Security */}
      <section className="py-20 max-w-7xl mx-auto px-4">
        <h2 className="text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">{t('features.trust.title')}</h2>
        <div className="grid md:grid-cols-4 gap-8">
          <div className="text-center">
            <Shield className="text-violet-600 dark:text-violet-400 mx-auto mb-4" size={40} />
            <h3 className="text-gray-900 dark:text-white font-semibold mb-2">{t('features.trust.euCompliant.title')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('features.trust.euCompliant.desc')}</p>
          </div>
          <div className="text-center">
            <Eye className="text-violet-600 dark:text-violet-400 mx-auto mb-4" size={40} />
            <h3 className="text-gray-900 dark:text-white font-semibold mb-2">{t('features.trust.explainableAI.title')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('features.trust.explainableAI.desc')}</p>
          </div>
          <div className="text-center">
            <Lock className="text-violet-600 dark:text-violet-400 mx-auto mb-4" size={40} />
            <h3 className="text-gray-900 dark:text-white font-semibold mb-2">{t('features.trust.bankGrade.title')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('features.trust.bankGrade.desc')}</p>
          </div>
          <div className="text-center">
            <ShieldCheck className="text-violet-600 dark:text-violet-400 mx-auto mb-4" size={40} />
            <h3 className="text-gray-900 dark:text-white font-semibold mb-2">{t('features.trust.humanReview.title')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('features.trust.humanReview.desc')}</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 py-20" style={{ background: 'linear-gradient(135deg, #4c1d95 0%, #4338ca 50%, #1e3a8a 100%)' }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">
            {t('features.cta.title')}
          </h2>

          <p className="text-xl text-gray-200 mb-10">
            {t('features.cta.subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/register')}
              className="bg-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all shadow-lg flex items-center justify-center gap-2"
              style={{ color: '#4c1d95' }}
            >
              {t('features.cta.startFree')}
              <ArrowRight size={20} />
            </button>

            <button
              onClick={() => navigate('/pricing')}
              className="bg-white/10 backdrop-blur-md text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/20 transition-all border border-white/30"
            >
              {t('features.cta.viewBundles')}
            </button>
          </div>
        </div>
      </section>


      {/* Footer */}
      <footer className="relative z-10 backdrop-blur-xl border-t border-gray-200 dark:border-white/10 py-8 bg-white/70 dark:bg-[rgba(9,9,15,0.7)]">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('features.footer')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default FeaturesPage;